import { motion } from '@/lib/motion';
import { ArrowLeft, Clock } from 'lucide-react';
import { Link, useParams, Navigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Seo } from '@/lib/seo';

import { getPostBySlug } from './posts';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** Minimal markdown renderer — supports ##/### headings, **bold**, tables, lists, --- */
function renderMarkdown(md: string): JSX.Element[] {
  const lines = md.split('\n');
  const elements: JSX.Element[] = [];
  let tableBuffer: string[] = [];
  let listBuffer: string[] = [];
  let key = 0;

  function flushList(): void {
    if (!listBuffer.length) return;
    elements.push(
      <ul key={key++} className="my-4 space-y-1 pl-5 text-sm text-muted-foreground list-disc">
        {listBuffer.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: inlineFormat(item.replace(/^[-*]\s+/, '')) }} />
        ))}
      </ul>,
    );
    listBuffer = [];
  }

  function flushTable(): void {
    if (!tableBuffer.length) return;
    const rows = tableBuffer.filter((r) => !r.match(/^[\s|:-]+$/));
    const [header, ...body] = rows;
    if (!header) return;
    const cols = header.split('|').map((c) => c.trim()).filter(Boolean);
    elements.push(
      <div key={key++} className="my-6 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              {cols.map((c, i) => (
                <th key={i} className="py-2 pr-4 text-left font-semibold" dangerouslySetInnerHTML={{ __html: inlineFormat(c) }} />
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, ri) => {
              const cells = row.split('|').map((c) => c.trim()).filter(Boolean);
              return (
                <tr key={ri} className="border-b border-border/50">
                  {cells.map((c, ci) => (
                    <td key={ci} className="py-2 pr-4 text-muted-foreground" dangerouslySetInnerHTML={{ __html: inlineFormat(c) }} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>,
    );
    tableBuffer = [];
  }

  function inlineFormat(text: string): string {
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`(.+?)`/g, '<code class="rounded bg-muted px-1 py-0.5 text-xs font-mono">$1</code>');
  }

  for (const line of lines) {
    if (line.startsWith('|')) {
      flushList();
      tableBuffer.push(line);
      continue;
    }
    if (tableBuffer.length) flushTable();

    if (line.match(/^[-*]\s+/)) {
      listBuffer.push(line);
      continue;
    }
    if (listBuffer.length) flushList();

    if (line.startsWith('### ')) {
      elements.push(<h3 key={key++} className="mt-6 mb-2 text-lg font-semibold">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={key++} className="mt-10 mb-3 text-2xl font-bold tracking-tight">{line.slice(3)}</h2>);
    } else if (line.startsWith('---')) {
      elements.push(<hr key={key++} className="my-8 border-border" />);
    } else if (line.trim()) {
      elements.push(
        <p key={key++} className="my-3 text-sm leading-7 text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: inlineFormat(line) }} />,
      );
    }
  }
  flushList();
  flushTable();
  return elements;
}

export function BlogPostPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug ?? '');

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <>
      <Seo
        title={`${post.title} — Magzit Blog`}
        description={post.description}
        path={`/blog/${post.slug}`}
      />

      <article>
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute left-1/2 top-0 h-[400px] w-[800px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-[var(--brand-accent)]/15 via-[var(--brand-accent)]/5 to-transparent blur-3xl" />
          </div>
          <div className="container py-10 sm:py-14">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-2xl"
            >
              <Button asChild variant="ghost" size="sm" className="-ml-2 mb-6 text-muted-foreground">
                <Link to="/blog">
                  <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to blog
                </Link>
              </Button>

              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="rounded-full bg-[var(--brand-accent)]/10 px-2.5 py-0.5 font-medium text-[var(--brand-accent)]">
                  {post.category}
                </span>
                <span>{formatDate(post.date)}</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {post.readingMinutes} min read
                </span>
              </div>

              <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                {post.title}
              </h1>
              <p className="mt-3 text-muted-foreground">{post.description}</p>
            </motion.div>
          </div>
        </section>

        {/* Body */}
        <section className="py-10 sm:py-12">
          <div className="container">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mx-auto max-w-2xl"
            >
              {renderMarkdown(post.content)}
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border py-10 sm:py-12">
          <div className="container">
            <div className="mx-auto max-w-xl rounded-2xl border border-[var(--brand-accent)]/20 bg-gradient-to-br from-[var(--brand-accent)]/10 to-transparent p-8 text-center">
              <h2 className="text-2xl font-bold">Ready to answer every call?</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Set up your AI receptionist in under 10 minutes. No long-term contract.
              </p>
              <Button asChild size="lg" className="mt-6">
                <Link to="/signup">Get started</Link>
              </Button>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
